import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import Recommendations from '../components/Recommendations';
import SEO from '../components/SEO';
import { DISCOVERY_CALL_URL } from '../constants/site';
import { ORGANIZATION_SCHEMA, FAQ_SCHEMA } from '../constants/seo';

const HOME_JSONLD = {
  '@context': 'https://schema.org',
  '@graph': [ORGANIZATION_SCHEMA, FAQ_SCHEMA],
};

const HomePage = () => {
  // 4 steps of the founder's story — horizontal timeline in the MY STORY section.
  const storyPoints = [
    { id: 'applications', num: '01', title: '120 applications', desc: 'Airline CV sent to private operators. Almost no replies.' },
    { id: 'training',     num: '02', title: 'A paid VIP training', desc: 'Service skills I already had. Still no interviews.' },
    { id: 'conversation', num: '03', title: 'One sit-down', desc: 'A working VIP flight attendant rebuilt my profile.' },
    { id: 'hired',        num: '04', title: 'Hired by 2 operators', desc: 'Profile views came in straight away, then two offers.' },
  ];

  // 3 cards for the dark "The Call" section — reuses the same bordered dark
  // box layout as the current "Are you ready for private aviation?" section.
  const callBoxes = [
    {
      label: 'Your profile',
      desc: "How operators will read your airline or hospitality background, and what's holding it back.",
    },
    {
      label: 'Your targets',
      desc: 'Which operators, aircraft and roles make sense for you.',
    },
    {
      label: 'Your plan',
      desc: 'Clear next steps, and whether training or mentoring fits you.',
    },
  ];

  return (
    <div data-testid="home-page" className="min-h-screen">
      <SEO
        title="Private Aviation Cabin Crew Training — Live Online | Gate 33"
        description="Live 3-day online training for commercial crew, hospitality professionals, yacht crew and career changers moving into private aviation. Delivered by a working corporate flight attendant."
        path="/"
        keywords="Private Aviation Cabin Crew Training, VIP Flight Attendant Training, Private Jet Cabin Attendant Course, Corporate Flight Attendant Training, Yacht Crew to Private Aviation, Hospitality to Private Aviation, Business Aviation Cabin Crew"
        jsonLd={HOME_JSONLD}
      />

      {/* ===== 1) HERO ===== */}
      <section data-testid="hero-section" className="relative min-h-[90vh] flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_gate33-preview/artifacts/kyukeuh3_65e0cd8d755b2661fafcedd4_65ca66f1e325b815020944eb_global-5000%2520EXT.webp"
            alt="Gate 33 — Break into private aviation from anywhere"
            title="Gate 33 — Break into private aviation from anywhere"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FDFBF7] via-[#FDFBF7]/90 to-[#FDFBF7]/50 lg:to-[#FDFBF7]/20" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-24">
          <div className="max-w-4xl">
            <span className="overline inline-block mb-6 animate-fade-in-up">
              Private Aviation Career Training
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight mb-6 animate-fade-in-up stagger-1">
              Break Into Private Aviation<br />— From Anywhere.
            </h1>
            <p className="text-lg text-[#1A1A1A] leading-relaxed mb-10 animate-fade-in-up stagger-2 max-w-xl">
              The complete Gate 33 training, now fully online. Built for commercial crew, hospitality professionals and career changers who want the real operational standards of business aviation.
            </p>
            <div className="flex flex-col items-start gap-4 animate-fade-in-up stagger-3">
              <a
                href={DISCOVERY_CALL_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-button"
                onClick={() => window.gtag?.('event', 'discovery_call_clicked', { location: 'home_hero' })}
                className="btn-primary inline-flex items-center justify-center gap-2"
              >
                Book a Free Discovery Call
                <ArrowRight size={16} />
              </a>
              <Link
                to="/operational-assessment"
                data-testid="hero-quiz-cta"
                className="text-sm text-[#1A1A1A] underline underline-offset-4 decoration-[#C5A880] hover:decoration-[#0F0F0F]"
              >
                Not ready yet? Take the free readiness assessment →
              </Link>
            </div>

            {/* Credential strip — founder profile */}
            <div className="mt-10 flex items-center gap-4 max-w-xl animate-fade-in-up stagger-4">
              <img
                src="https://customer-assets-eiarnc6j.emergentagent.net/job_gate33-preview/artifacts/dq20hpml_Screenshot%202026-07-18%20at%2020.51.57.png"
                alt="Nicolas Perot dos Santos — Founder of Gate 33"
                className="w-14 h-14 rounded-full object-cover object-center border-2 border-[#C5A880] flex-shrink-0"
              />
              <p className="text-xs sm:text-sm text-[#1A1A1A] leading-snug">
                <span className="font-semibold">Founded by Nicolas Perot dos Santos</span> — Qatar Airways, Etihad, today corporate flight attendant on <span className="font-semibold">Bombardier Global and Challenger series</span> aircraft.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 2) MY STORY — centered header · horizontal timeline · closing row (photo + pull quote + CTA) ===== */}
      <section data-testid="story-section" className="pt-20 md:pt-28 pb-20 md:pb-28 bg-[#FDFBF7]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">

          {/* 1. HEADER — centered */}
          <div className="text-center max-w-[640px] mx-auto">
            <span className="overline inline-block mb-4">My Story</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-[#0F0F0F] mb-5">
              120 applications. Almost no answers.
            </h2>
            <p className="text-[#5A5A5A] leading-relaxed text-[15px]">
              From Qatar Airways and Etihad to private aviation — here&rsquo;s what actually got me hired.
            </p>
          </div>

          {/* 2. HORIZONTAL TIMELINE — desktop */}
          <div data-testid="story-timeline" className="hidden md:block mt-20">
            <div className="relative">
              {/* Thin gold line running behind the numbers */}
              <div
                aria-hidden="true"
                className="absolute left-0 right-0 top-[26px] h-px bg-[#C5A880]/60"
              />
              {/* Filled gold dot marking the end of the line (on step 04) */}
              <div
                aria-hidden="true"
                className="absolute right-0 top-[22px] w-2 h-2 rounded-full bg-[#C5A880]"
              />

              <div className="grid grid-cols-4 gap-6 relative">
                {storyPoints.map((p, i) => {
                  const isOutcome = i === storyPoints.length - 1;
                  return (
                    <div key={p.id} className="flex flex-col items-start">
                      {/* Serif number sitting on the line, on a cream background so the line appears to pass behind */}
                      <span
                        className={`font-heading text-[44px] leading-[54px] px-3 bg-[#FDFBF7] ${
                          isOutcome ? 'text-[#0F0F0F]' : 'text-[#C5A880]'
                        } -ml-3`}
                      >
                        {p.num}
                      </span>
                      <div className="mt-5 max-w-[220px]">
                        <h3
                          className={`font-heading text-[18px] leading-snug mb-2 ${
                            isOutcome ? 'text-[#0F0F0F]' : 'text-[#0F0F0F]'
                          }`}
                        >
                          {p.title}
                        </h3>
                        <p className="text-[13px] text-[#5A5A5A] leading-relaxed">{p.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* 2. VERTICAL TIMELINE — mobile */}
          <div className="md:hidden mt-14">
            <div className="relative pl-10">
              {/* Vertical gold line down the left */}
              <div
                aria-hidden="true"
                className="absolute left-[18px] top-3 bottom-3 w-px bg-[#C5A880]/60"
              />
              {/* Bottom dot */}
              <div
                aria-hidden="true"
                className="absolute left-[14px] bottom-[6px] w-2 h-2 rounded-full bg-[#C5A880]"
              />
              <div className="space-y-10">
                {storyPoints.map((p, i) => {
                  const isOutcome = i === storyPoints.length - 1;
                  return (
                    <div key={p.id} className="relative">
                      <span
                        className={`absolute -left-10 -top-1 font-heading text-[30px] leading-none px-1.5 bg-[#FDFBF7] ${
                          isOutcome ? 'text-[#0F0F0F]' : 'text-[#C5A880]'
                        }`}
                      >
                        {p.num}
                      </span>
                      <div>
                        <h3 className="font-heading text-[17px] leading-snug mb-1.5 text-[#0F0F0F]">{p.title}</h3>
                        <p className="text-[13px] text-[#5A5A5A] leading-relaxed">{p.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* 3. CLOSING ROW — photo LEFT · quote + CTA RIGHT */}
          <div className="mt-20 md:mt-24">
            <div className="grid grid-cols-1 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-10 md:gap-14 items-center">
              {/* LEFT — portrait with thin gold offset border */}
              <div className="relative w-full max-w-[360px] md:max-w-none mx-auto">
                <div className="relative aspect-[4/5] overflow-hidden">
                  <img
                    src="https://customer-assets-eiarnc6j.emergentagent.net/job_gate33-preview/artifacts/dq20hpml_Screenshot%202026-07-18%20at%2020.51.57.png"
                    alt="Nicolas Perot dos Santos — Founder of Gate 33"
                    title="Nicolas Perot dos Santos — Founder of Gate 33"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
                <div
                  aria-hidden="true"
                  className="absolute -bottom-4 -right-4 md:-bottom-6 md:-right-6 w-full h-full border border-[#C5A880] pointer-events-none"
                />
              </div>

              {/* RIGHT — pull quote + paragraph + CTAs */}
              <div>
                <blockquote className="italic font-heading text-2xl sm:text-3xl leading-snug text-[#0F0F0F] border-l-2 border-[#C5A880] pl-5">
                  My experience was never the problem. My positioning was.
                </blockquote>
                <p className="mt-6 text-[#5A5A5A] leading-relaxed text-[15px] max-w-xl">
                  Whether you come from an airline, a luxury hotel, a fine-dining restaurant or a private household, operators hire people who understand the operational reality of the job and know how to show it. That&rsquo;s what Gate 33 gives you.
                </p>
                <div className="mt-8 flex flex-wrap items-center gap-6">
                  <a
                    href={DISCOVERY_CALL_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="story-discovery-cta"
                    onClick={() => window.gtag?.('event', 'discovery_call_clicked', { location: 'home_story' })}
                    className="btn-primary inline-flex items-center gap-2"
                  >
                    Book a Free Discovery Call <ArrowRight size={16} />
                  </a>
                  <Link
                    to="/insights/field-notes/120-applications"
                    data-testid="story-read-link"
                    className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] text-[#C5A880] font-semibold hover:text-[#0F0F0F] transition-colors"
                  >
                    Read the full story <ArrowRight size={12} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 3) SEGMENTS — moved here from further down, eyebrow updated ===== */}
      <section data-testid="home-segmentation" className="bg-[#FDFBF7] py-20 md:py-24 border-b border-[#E5E0D8]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mb-10 text-center mx-auto">
            <span className="overline inline-block mb-4">Whatever your background</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-[#0F0F0F]">
              Private aviation hires from more places than you think.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { slug: 'commercial-cabin-crew', label: 'Commercial cabin crew', line: 'You fly already. The operational reality is different.' },
              { slug: 'hospitality', label: 'Hotels & fine dining', line: "You have the service instinct. Here's what's missing." },
              { slug: 'private-household', label: 'Private households & villas', line: "Discretion and anticipation transfer. Operations don't." },
            ].map((s) => (
              <Link
                key={s.slug}
                to={`/transitions/${s.slug}`}
                data-testid={`home-segment-${s.slug}`}
                className="group bg-white border border-[#E5E0D8] p-6 hover:border-[#C5A880] transition-colors flex flex-col"
              >
                <span className="text-[10px] uppercase tracking-[0.22em] text-[#C5A880] font-semibold">Segment</span>
                <h3 className="font-heading text-lg md:text-xl text-[#0F0F0F] mt-2 mb-3 leading-snug">{s.label}</h3>
                <p className="text-sm text-[#5A5A5A] leading-relaxed mb-4 flex-grow">{s.line}</p>
                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[#C5A880] font-semibold group-hover:gap-2 transition-all">
                  See the guide <ArrowRight size={12} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===== 4) THE CALL — reuses the dark "Are you ready" section component ===== */}
      <section data-testid="home-call" className="section-padding bg-[#0F0F0F] text-[#FDFBF7]">
        <div className="container-custom max-w-4xl text-center">
          <Sparkles size={26} className="text-[#C5A880] mx-auto mb-4" strokeWidth={1.5} />
          <span className="text-xs uppercase tracking-[0.22em] text-[#C5A880] font-semibold inline-block mb-4">
            Free · 45 minutes · One-to-one with Nicolas
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mb-6 text-[#FDFBF7]">
            The conversation that changed my career.<br />Now it&rsquo;s yours.
          </h2>
          <p className="text-[#9A9A9A] leading-relaxed mb-10 max-w-2xl mx-auto">
            Whether you come from a cabin, a hotel, a restaurant or a private household, I&rsquo;ll do for you what a VIP flight attendant once did for me: look at your profile through an operator&rsquo;s eyes and tell you exactly what to change.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10 max-w-3xl mx-auto text-left">
            {callBoxes.map((b) => (
              <div key={b.label} className="border border-[#C5A880]/30 bg-[#1A1A1A] p-5">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#C5A880] mb-3">{b.label}</p>
                <p className="text-sm text-[#FDFBF7] leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>

          <a
            href={DISCOVERY_CALL_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="home-call-cta"
            onClick={() => window.gtag?.('event', 'discovery_call_clicked', { location: 'home_the_call' })}
            className="inline-flex items-center gap-2 bg-[#C5A880] text-[#0F0F0F] px-10 py-4 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#FDFBF7] transition-all duration-300"
          >
            Book my free discovery call
            <ArrowRight size={16} />
          </a>
          <p className="mt-6 text-xs text-[#9A9A9A]">
            Free · No commitment · Online · No aviation experience required
          </p>
        </div>
      </section>

      {/* ===== 5) HOW WE WORK WITH YOU — reuses the segment card style, 2 cards ===== */}
      <section data-testid="home-how-we-work" className="bg-white py-20 md:py-24 border-b border-[#E5E0D8]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mb-10 text-center mx-auto">
            <span className="overline inline-block mb-4">How we work with you</span>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-[#0F0F0F]">
              Not another service course.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Link
              to="/academy"
              data-testid="home-how-flagship"
              className="group bg-[#FDFBF7] border border-[#E5E0D8] p-8 md:p-10 hover:border-[#C5A880] transition-colors flex flex-col"
            >
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#C5A880] font-semibold">Flagship program</span>
              <h3 className="font-heading text-xl md:text-2xl text-[#0F0F0F] mt-3 mb-4 leading-snug">3-Day Online Training</h3>
              <p className="text-sm text-[#5A5A5A] leading-relaxed mb-6 flex-grow">
                Three live days with Nicolas on what private aviation actually requires, built for cabin crew and hospitality professionals alike: 14 modules based on real VistaJet, NetJets and Global/Challenger operations, from catering and aircraft mastery to crisis handling and interview prep.
              </p>
              <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[#C5A880] font-semibold group-hover:gap-2 transition-all">
                See the program <ArrowRight size={12} />
              </span>
            </Link>
            <Link
              to="/mentorship"
              data-testid="home-how-mentorship"
              className="group bg-[#FDFBF7] border border-[#E5E0D8] p-8 md:p-10 hover:border-[#C5A880] transition-colors flex flex-col"
            >
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#C5A880] font-semibold">End-to-end support</span>
              <h3 className="font-heading text-xl md:text-2xl text-[#0F0F0F] mt-3 mb-4 leading-snug">3-Month Mentorship</h3>
              <p className="text-sm text-[#5A5A5A] leading-relaxed mb-6 flex-grow">
                Three months of 1:1 guidance to reposition your profile, whatever your background, target the right operators and prepare for the interviews that matter.
              </p>
              <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[#C5A880] font-semibold group-hover:gap-2 transition-all">
                See mentoring <ArrowRight size={12} />
              </span>
            </Link>
          </div>
          <p data-testid="home-how-paypal-line" className="text-center text-xs text-[#5A5A5A] mt-8 max-w-2xl mx-auto">
            Take both together as The Complete Path and save €200. Training and Mentorship can be paid in up to 24 instalments with PayPal.
          </p>
        </div>
      </section>

      {/* ===== 6) FOUNDER CREDENTIALS — kept as-is; only stat + button copy updated ===== */}
      <section data-testid="about-snippet-section" className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <div className="aspect-[3/4] image-hover">
                <img
                  src="https://customer-assets.emergentagent.com/job_gate33-preview/artifacts/k0a6b12n_bram-kahmann-exxaero-shoot-food_-21.jpg"
                  alt="Gate 33 — real operational catering preparation in a private jet galley"
                  title="Gate 33 — the operational reality behind private aviation"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-7 lg:pl-12">
              <span className="overline inline-block mb-4">The Expertise Behind Gate 33</span>
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight mb-6">
                Built on real operational experience.
              </h2>
              <p className="text-[#5A5A5A] leading-relaxed mb-6">
                Gate 33 was founded by Nicolas Perot dos Santos — Qatar Airways, Etihad, and today
                a corporate flight attendant on Bombardier Global and Challenger series aircraft
                for owner, corporate and charter operations.
              </p>
              <p className="text-[#5A5A5A] leading-relaxed mb-6">
                This dual background provides a unique perspective: understanding what airline and hospitality
                professionals bring, and what business aviation actually requires — real operational expertise,
                not theoretical training.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="p-4 bg-[#FDFBF7] border border-[#E5E0D8]">
                  <p className="text-2xl font-heading text-[#C5A880] mb-1">10+</p>
                  <p className="text-xs text-[#5A5A5A] uppercase tracking-wider">Years in Aviation</p>
                </div>
                <div className="p-4 bg-[#FDFBF7] border border-[#E5E0D8]">
                  <p className="text-2xl font-heading text-[#C5A880] mb-1">~100</p>
                  <p className="text-xs text-[#5A5A5A] uppercase tracking-wider">Candidates trained</p>
                </div>
              </div>
              <a
                href={DISCOVERY_CALL_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-read-more"
                onClick={() => window.gtag?.('event', 'discovery_call_clicked', { location: 'home_founder' })}
                className="btn-secondary inline-flex items-center gap-2"
              >
                Book a discovery call
                <ArrowRight size={14} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ===== 7) TESTIMONIALS — carousel only. Standalone Matteo/Alexandra quotes removed. ===== */}
      <Recommendations
        variant="light"
        centered
        compact
        eyebrow="Industry Perspective"
        title="The Reality Behind The Transition"
        testId="home-recommendations"
      />

      {/* ===== 8) FINAL CTA — button priority swapped ===== */}
      <section data-testid="cta-section" className="section-dark py-12 md:py-16 border-b border-[#C5A880]/40">
        <div className="container-custom text-center">
          <span className="text-xs uppercase tracking-[0.2em] font-medium text-[#C5A880] inline-block mb-4">
            Ready to move?
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mb-6 text-[#FDFBF7]">
            Ready to enter private aviation?
          </h2>
          <p className="text-[#9A9A9A] max-w-xl mx-auto mb-10">
            Book a discovery call to talk it through directly, or start with the free assessment.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a
              href={DISCOVERY_CALL_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-book-session"
              onClick={() => window.gtag?.('event', 'discovery_call_clicked', { location: 'home_final_cta' })}
              className="inline-flex items-center gap-2 bg-[#C5A880] text-[#0F0F0F] px-10 py-4 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#FDFBF7] transition-all duration-300"
            >
              Book a Discovery Call
              <ArrowRight size={16} />
            </a>
            <Link
              to="/operational-assessment"
              data-testid="cta-quiz"
              className="inline-flex items-center gap-2 border border-[#C5A880] text-[#C5A880] px-10 py-4 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#C5A880] hover:text-[#0F0F0F] transition-all duration-300"
            >
              Take the Assessment <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
